import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ArrowLeft, Search, X } from 'lucide-react'
import { type ChangeEvent, type KeyboardEvent, useEffect, useId, useRef, useState } from 'react'

import { repetirBusca, validarBusca } from '@/lib/buscaNaUrl'
import { useRecorte } from '@/lib/recorte'
import { buscaPorMunicipio, registrarUso } from '@/lib/uso'
import { cn } from '@/lib/utils'

import {
  consultaDeSugestoes,
  MAXIMO_DE_SUGESTOES,
  type MunicipioSugerido,
  useMunicipioDoCodigo,
  useSugestoes,
} from '@/components/busca/sugestoes'
import {
  consultaDeMunicipio,
  normalizarRecibo,
  semCaixaNemAcento,
  temAlgarismo,
} from '@/components/busca/textoDaBusca'
import { useAtraso } from '@/components/busca/useAtraso'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export const NOME_DA_BUSCA = 'Buscar por município ou número do recibo do CAR'
const EXEMPLO_DA_BUSCA = 'Busque por Município ou Nº do CAR (ex: RJ-3300159-...)'

const BUSCANDO = 'Buscando municípios…'
const ERRO_NAS_SUGESTOES = 'Não foi possível carregar as sugestões. Tente de novo.'
const SEM_MUNICIPIO = 'Nenhum município encontrado com esse nome.'
const SELECIONE_NA_LISTA = 'Selecione um município na lista.'

/** A pausa entre as teclas antes de pedir sugestões, para não pedir a cada letra. */
const PAUSA_ENTRE_TECLAS_MS = 250

interface Mensagem {
  texto: string
  /** `aviso` é a resposta ao Enter; `estado` é o que a lista está fazendo. */
  tipo: 'aviso' | 'estado' | 'erro'
}

interface BarraDeBuscaProps {
  /**
   * Abaixo de 1280px o campo só aparece depois do atalho do cabeçalho; daí a
   * pergunta. No desktop o campo está sempre à vista, qualquer que seja o valor.
   */
  aberta?: boolean
  /** Fecha o campo aberto pelo atalho (a seta de voltar e o Esc sem lista). */
  aoFechar?: () => void
  /** A busca foi aplicada (município escolhido, recibo com Enter): o campo pode fechar. */
  aoAplicar?: () => void
}

/**
 * A barra de busca do cabeçalho: um campo só, que entende município e recibo
 * pelo que a pessoa digita. Texto com algarismo é recibo (nenhum nome de
 * município do IBGE tem algarismo); sem algarismo, é município, e a lista
 * sugere "Nome-UF" do Brasil inteiro enquanto se digita.
 *
 * O que a busca aplica vai para a URL (`?uf=<UF>&municipio=<código>`,
 * o mesmo município do filtro, ou `?recibo=<recibo>`, que sai do lugar do
 * município): quem reage ao recorte — o mapa, o painel, os indicadores — lê a
 * URL, e recarregar a página reaplica a busca. A barra só escolhe; não sabe o que
 * o mapa faz com a escolha.
 *
 * É um combobox do padrão WAI-ARIA 1.2: o foco fica sempre no campo, e a opção
 * ativa vai em `aria-activedescendant`. Seta para baixo e para cima movem a
 * opção, Enter escolhe, Esc fecha a lista sem mudar nada, e o clique também
 * escolhe. Montado em cima do `input` do shadcn, sem `cmdk`: quem filtra é o
 * servidor, e o foco não pode sair do campo.
 */
export function BarraDeBusca({ aberta = true, aoFechar, aoAplicar }: BarraDeBuscaProps) {
  const idDaLista = useId()
  const queryClient = useQueryClient()
  const navegar = useNavigate()
  const buscaDaUrl: Record<string, unknown> = useSearch({ strict: false })
  const { municipio: municipioDaUrl, recibo: reciboDaUrl } = validarBusca(buscaDaUrl)
  const { recorte, escolherMunicipio, limpar: limparRecorte } = useRecorte()

  const campo = useRef<HTMLInputElement>(null)
  const [listaAberta, setListaAberta] = useState(false)
  const [ativa, setAtiva] = useState(-1)
  const [aviso, setAviso] = useState<Mensagem | null>(null)

  // ── O campo reflete a URL ────────────────────────────────────────────────
  // Município escolhido nesta sessão: o rótulo já se sabe, sem perguntar à API.
  const [rotulosConhecidos] = useState(() => new Map<number, string>())
  const municipioDoCodigo = useMunicipioDoCodigo(
    municipioDaUrl !== undefined && !rotulosConhecidos.has(municipioDaUrl)
      ? municipioDaUrl
      : undefined
  )
  const rotuloDaApi =
    municipioDoCodigo.data === undefined
      ? undefined
      : `${municipioDoCodigo.data.nome}-${municipioDoCodigo.data.ufSigla}`
  let textoDaUrl = ''
  if (reciboDaUrl !== undefined) textoDaUrl = reciboDaUrl
  else if (municipioDaUrl !== undefined)
    textoDaUrl = rotulosConhecidos.get(municipioDaUrl) ?? rotuloDaApi ?? ''

  // O que a pessoa digitou e ainda não aplicou; `null` é "nada digitado", e o
  // campo mostra o que a URL diz. Quando a busca da URL muda — escolha, recibo,
  // o x, voltar do navegador, fechar o painel do imóvel —, o rascunho é
  // descartado e o campo acompanha a URL.
  const chaveDaUrl =
    reciboDaUrl !== undefined
      ? `recibo:${reciboDaUrl}`
      : municipioDaUrl !== undefined
        ? `municipio:${municipioDaUrl}`
        : ''
  const [chaveVista, setChaveVista] = useState(chaveDaUrl)
  const [rascunho, setRascunho] = useState<string | null>(null)
  if (chaveDaUrl !== chaveVista) {
    setChaveVista(chaveDaUrl)
    setRascunho(null)
  }
  const texto = rascunho ?? textoDaUrl

  // O que está no campo agora, para o Enter descartar uma resposta de um texto
  // que a pessoa já trocou.
  const textoAtual = useRef(texto)
  useEffect(() => {
    textoAtual.current = texto
  }, [texto])

  // ── Sugestões ────────────────────────────────────────────────────────────
  const consultaAtual = consultaDeMunicipio(texto)
  const textoComPausa = useAtraso(texto, PAUSA_ENTRE_TECLAS_MS)
  const consultaPausada = consultaDeMunicipio(textoComPausa)
  const sugestoes = useSugestoes(listaAberta ? consultaPausada : null)
  // Só vale a resposta do texto que está no campo agora: dentro da pausa entre
  // as teclas a consulta ainda é a do texto anterior, e a lista antiga não pode
  // ficar à mão para seta e Enter escolherem um município sem relação com o que
  // foi digitado.
  const respostaVale =
    listaAberta &&
    consultaAtual !== null &&
    consultaPausada !== null &&
    consultaAtual.texto === consultaPausada.texto &&
    consultaAtual.uf === consultaPausada.uf
  const dados = respostaVale ? sugestoes.data : undefined
  const opcoes: MunicipioSugerido[] = (dados ?? []).slice(0, MAXIMO_DE_SUGESTOES)

  let mensagem: Mensagem | null = aviso
  if (mensagem === null && listaAberta && consultaAtual !== null) {
    if (respostaVale && sugestoes.isError) mensagem = { texto: ERRO_NAS_SUGESTOES, tipo: 'erro' }
    else if (dados === undefined) mensagem = { texto: BUSCANDO, tipo: 'estado' }
    else if (dados.length === 0) mensagem = { texto: SEM_MUNICIPIO, tipo: 'estado' }
  }
  const expandida = opcoes.length > 0

  const idDaOpcao = (indice: number) => `${idDaLista}-opcao-${indice}`

  // ── Foco ao abrir o campo pelo atalho ────────────────────────────────────
  const estavaAberta = useRef(aberta)
  useEffect(() => {
    if (aberta && !estavaAberta.current) campo.current?.focus()
    estavaAberta.current = aberta
  }, [aberta])

  // ── Ações ────────────────────────────────────────────────────────────────
  function fecharLista() {
    setListaAberta(false)
    setAtiva(-1)
    setAviso(null)
  }

  function gravarNaUrl(busca: { municipio?: { codigo: number; uf: string }; recibo?: string }) {
    // A mesma busca que já está na URL não muda a URL: o mapa é avisado por
    // outro caminho, para voltar ao lugar e reabrir o aviso.
    const igual =
      busca.municipio?.codigo === municipioDaUrl &&
      (busca.recibo === undefined
        ? reciboDaUrl === undefined
        : reciboDaUrl !== undefined &&
          normalizarRecibo(reciboDaUrl) === normalizarRecibo(busca.recibo))
    if (igual && (busca.municipio !== undefined || busca.recibo !== undefined)) {
      repetirBusca()
      // Link só com o município: a repetição completa o Estado.
      if (busca.municipio !== undefined && busca.municipio.uf !== recorte.uf) {
        escolherMunicipio(busca.municipio)
      }
      return
    }
    // O município da barra é o mesmo do filtro: escolhê-lo preenche também o
    // Estado, e ele toma o lugar do recibo.
    if (busca.municipio !== undefined) {
      escolherMunicipio(busca.municipio)
      return
    }
    // O recibo toma o lugar do território da barra: sai o município e sai o
    // Estado, que pode ter vindo com ele. Um recibo de outro estado sob o Estado
    // antigo daria um recorte vazio. Bioma e valor não são da barra e ficam.
    void navegar({
      to: '/',
      search: (anterior: Record<string, unknown>) => ({
        ...anterior,
        uf: undefined,
        municipio: undefined,
        recibo: busca.recibo,
      }),
    })
  }

  function escolher(municipio: MunicipioSugerido) {
    rotulosConhecidos.set(municipio.codigo, municipio.rotulo)
    setRascunho(municipio.rotulo)
    fecharLista()
    registrarUso(buscaPorMunicipio(municipio.codigo))
    gravarNaUrl({ municipio: { codigo: municipio.codigo, uf: municipio.uf } })
    aoAplicar?.()
  }

  function aplicarRecibo(bruto: string) {
    const recibo = normalizarRecibo(bruto)
    setRascunho(recibo)
    fecharLista()
    // Só o tipo da busca: o número do recibo nunca sai do navegador no registro.
    registrarUso({ tipo: 'busca', busca: 'recibo' })
    gravarNaUrl({ recibo })
    aoAplicar?.()
  }

  // O x faz o mesmo que o Limpar dos filtros: a busca da barra e todos os filtros
  // de dados voltam ao início (URL `/`). Camadas e comparação não são do recorte
  // e ficam como estão. Apagar o texto à mão não passa por aqui e não muda a URL.
  function limpar() {
    setRascunho('')
    fecharLista()
    limparRecorte()
    campo.current?.focus()
  }

  async function aoTecarEnter() {
    const digitado = texto
    if (digitado.trim() === '') return
    if (temAlgarismo(digitado)) {
      aplicarRecibo(digitado)
      return
    }
    const consulta = consultaDeMunicipio(digitado)
    if (consulta === null) return

    // O Enter não espera a pausa entre as teclas: pede a lista deste texto
    // agora (ou usa a que já está no cache).
    let encontrados: MunicipioSugerido[]
    try {
      encontrados = await queryClient.fetchQuery(consultaDeSugestoes(consulta))
    } catch {
      if (textoAtual.current !== digitado) return
      setListaAberta(true)
      setAviso({ texto: ERRO_NAS_SUGESTOES, tipo: 'erro' })
      return
    }
    if (textoAtual.current !== digitado) return

    const alvo = semCaixaNemAcento(digitado.trim())
    const igual = encontrados.find((m) => semCaixaNemAcento(m.rotulo) === alvo)
    const unico = encontrados.length === 1 ? encontrados[0] : undefined
    const escolhido = igual ?? unico
    if (escolhido !== undefined) {
      escolher(escolhido)
      return
    }
    setListaAberta(true)
    setAtiva(-1)
    setAviso(
      encontrados.length === 0
        ? { texto: SEM_MUNICIPIO, tipo: 'aviso' }
        : { texto: SELECIONE_NA_LISTA, tipo: 'aviso' }
    )
  }

  function aoDigitar(evento: ChangeEvent<HTMLInputElement>) {
    setRascunho(evento.target.value)
    setListaAberta(true)
    setAtiva(-1)
    setAviso(null)
  }

  function aoTecar(evento: KeyboardEvent<HTMLInputElement>) {
    switch (evento.key) {
      case 'ArrowDown':
        evento.preventDefault()
        if (!listaAberta) {
          setListaAberta(true)
        } else if (opcoes.length > 0) {
          setAtiva((atual) => (atual + 1) % opcoes.length)
        }
        break
      case 'ArrowUp':
        evento.preventDefault()
        if (opcoes.length > 0) {
          setAtiva((atual) => (atual <= 0 ? opcoes.length - 1 : atual - 1))
        }
        break
      case 'Enter': {
        evento.preventDefault()
        const opcaoAtiva = opcoes[ativa]
        if (expandida && opcaoAtiva !== undefined) escolher(opcaoAtiva)
        else void aoTecarEnter()
        break
      }
      case 'Escape':
        if (listaAberta || aviso !== null) {
          evento.preventDefault()
          fecharLista()
        } else if (aoFechar !== undefined) {
          evento.preventDefault()
          aoFechar()
        }
        break
    }
  }

  return (
    <div
      role="search"
      className={cn(
        'min-w-0 flex-1 items-center gap-2',
        aberta
          ? 'absolute inset-0 z-(--gef-camada-menu) flex bg-(--gef-cabecalho-fundo) px-(--gef-cabecalho-margem)'
          : 'hidden',
        'min-[1280px]:relative min-[1280px]:inset-auto min-[1280px]:z-auto min-[1280px]:flex',
        'min-[1280px]:bg-transparent min-[1280px]:px-0'
      )}
      onBlur={(evento) => {
        // O foco saiu da busca inteira (campo, x e lista): a lista fecha.
        if (!evento.currentTarget.contains(evento.relatedTarget)) fecharLista()
      }}
    >
      {aoFechar !== undefined && (
        <Button
          variant="ghost"
          size="icon"
          aria-label="Fechar a busca"
          onClick={aoFechar}
          className="flex-none min-[1280px]:hidden"
        >
          <ArrowLeft aria-hidden="true" />
        </Button>
      )}

      <div className="relative min-w-0 flex-1">
        <Input
          ref={campo}
          type="text"
          role="combobox"
          aria-label={NOME_DA_BUSCA}
          aria-autocomplete="list"
          aria-expanded={expandida}
          aria-controls={expandida ? idDaLista : undefined}
          aria-activedescendant={expandida && ativa >= 0 ? idDaOpcao(ativa) : undefined}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          enterKeyHint="search"
          placeholder={EXEMPLO_DA_BUSCA}
          value={texto}
          onChange={aoDigitar}
          onKeyDown={aoTecar}
          className={
            'h-(--gef-busca-altura) rounded-(--gef-busca-raio) border-0 bg-(--gef-busca-fundo) ' +
            'text-base font-semibold text-foreground placeholder:text-(--gef-busca-exemplo) ' +
            'pl-[calc(var(--gef-busca-recuo)+var(--gef-busca-icone-tamanho)+var(--spacing)*2)] ' +
            'pr-[calc(var(--gef-busca-limpar-lado)+var(--gef-busca-recuo))]'
          }
        />
        <Search
          aria-hidden="true"
          className={
            'pointer-events-none absolute top-1/2 left-(--gef-busca-recuo) -translate-y-1/2 ' +
            'size-(--gef-busca-icone-tamanho) text-(--gef-busca-icone-cor)'
          }
        />
        {/* Com a busca na URL o x existe mesmo com o campo vazio (rótulo do município
            ainda não resolvido ou ficha indisponível): é a saída para quem abriu o link. */}
        {(texto !== '' || chaveDaUrl !== '') && (
          <button
            type="button"
            aria-label="Limpar busca"
            onClick={limpar}
            className={
              'absolute top-1/2 right-1 inline-flex size-(--gef-busca-limpar-lado) -translate-y-1/2 ' +
              'cursor-pointer items-center justify-center rounded-md ' +
              'text-(--gef-busca-limpar-cor) [--gef-foco:var(--gef-link)]'
            }
          >
            <X aria-hidden="true" className="size-(--gef-busca-limpar-icone)" />
          </button>
        )}

        {/* A lista logo abaixo do campo, com a largura dele. O `status` está
            sempre no DOM, para o leitor de tela anunciar o texto que chega. */}
        <div
          className={cn(
            'absolute inset-x-0 top-full z-(--gef-camada-menu) mt-(--gef-busca-lista-afastamento)',
            'overflow-hidden rounded-(--gef-busca-lista-raio) text-foreground',
            mensagem !== null || expandida
              ? 'bg-(--gef-busca-lista-fundo) shadow-(--gef-busca-lista-sombra)'
              : 'pointer-events-none'
          )}
        >
          <div
            role="status"
            className={cn(
              'px-(--gef-busca-item-recuo) py-2 text-base',
              mensagem === null && 'hidden',
              mensagem?.tipo === 'aviso' &&
                'border-b border-warning-border bg-warning-soft text-warning',
              mensagem?.tipo === 'estado' && 'text-(--gef-busca-estado-cor)',
              mensagem?.tipo === 'erro' && 'text-(--gef-busca-estado-erro-cor)'
            )}
          >
            {mensagem?.texto}
          </div>
          {expandida && (
            <ul
              id={idDaLista}
              role="listbox"
              aria-label="Municípios sugeridos"
              className="m-0 list-none p-0"
              // Clicar numa opção não tira o foco do campo.
              onMouseDown={(evento) => evento.preventDefault()}
            >
              {opcoes.map((opcao, indice) => (
                <li
                  key={opcao.codigo}
                  id={idDaOpcao(indice)}
                  role="option"
                  aria-selected={indice === ativa}
                  onClick={() => escolher(opcao)}
                  onMouseEnter={() => setAtiva(indice)}
                  className={cn(
                    'flex min-h-(--gef-busca-item-altura) cursor-pointer items-center',
                    'px-(--gef-busca-item-recuo) text-base font-(--gef-busca-item-peso)',
                    'border-b border-(--gef-busca-item-divisa) last:border-b-0',
                    indice === ativa && 'bg-(--gef-busca-item-ativo-fundo)'
                  )}
                >
                  {opcao.rotulo}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
